import React, { useState, useEffect, useCallback } from "react";
import { FiSearch, FiX, FiLoader } from "react-icons/fi";

interface EnhancedSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  debounceMs?: number;
  isLoading?: boolean;
  disabled?: boolean;
  onClear?: () => void;
  showClearButton?: boolean;
  size?: "sm" | "md" | "lg";
}

const EnhancedSearchInput: React.FC<EnhancedSearchInputProps> = ({
  value,
  onChange,
  placeholder = "Search by name or email...",
  className = "",
  debounceMs = 300,
  isLoading = false,
  disabled = false,
  onClear,
  showClearButton = true,
  size = "md"
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  // Debounced onChange handler
  const debouncedOnChange = useCallback(
    (searchValue: string) => {
      const timeoutId = setTimeout(() => {
        onChange(searchValue);
      }, debounceMs);

      return () => clearTimeout(timeoutId);
    },
    [onChange, debounceMs]
  );

  // Update internal value when external value changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Handle debounced search
  useEffect(() => {
    if (internalValue !== value) {
      const cleanup = debouncedOnChange(internalValue);
      return cleanup;
    }
  }, [internalValue, value, debouncedOnChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
  };

  const handleClear = () => {
    setInternalValue("");
    onChange("");
    if (onClear) {
      onClear();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      handleClear();
    }
  };

  // Size-based styling
  const sizeClasses = {
    sm: {
      container: "h-8",
      input: "text-sm py-1 pl-8 pr-8",
      icon: "w-4 h-4",
      iconLeft: "left-2",
      iconRight: "right-2"
    },
    md: {
      container: "h-10",
      input: "text-sm py-2 pl-10 pr-10",
      icon: "w-4 h-4",
      iconLeft: "left-3",
      iconRight: "right-3"
    },
    lg: {
      container: "h-12",
      input: "text-base py-3 pl-12 pr-12",
      icon: "w-5 h-5",
      iconLeft: "left-3",
      iconRight: "right-3"
    }
  };

  const currentSize = sizeClasses[size];

  return (
    <div className={`relative ${currentSize.container} ${className}`}>
      {/* Search Icon */}
      <div className={`absolute inset-y-0 ${currentSize.iconLeft} flex items-center pointer-events-none`}>
        {isLoading ? (
          <FiLoader className={`${currentSize.icon} text-blue-500 animate-spin`} />
        ) : (
          <FiSearch className={`${currentSize.icon} text-gray-400`} />
        )}
      </div>

      {/* Input Field */}
      <input
        type="text"
        value={internalValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        className={`
          w-full ${currentSize.input} 
          border border-gray-300 rounded-lg
          bg-white
          placeholder-gray-500
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
          hover:border-gray-400
          disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
          ${isFocused ? "shadow-sm" : ""}
          ${isLoading ? "pr-16" : ""}
        `}
      />

      {/* Clear Button */}
      {showClearButton && internalValue && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          className={`
            absolute inset-y-0 ${currentSize.iconRight} flex items-center
            text-gray-400 hover:text-gray-600
            transition-colors duration-200
            focus:outline-none focus:text-gray-600
          `}
          aria-label="Clear search"
        >
          <FiX className={currentSize.icon} />
        </button>
      )}

      {/* Loading indicator (when not showing in search icon) */}
      {isLoading && internalValue && (
        <div className={`absolute inset-y-0 right-8 flex items-center`}>
          <FiLoader className={`${currentSize.icon} text-blue-500 animate-spin`} />
        </div>
      )}
    </div>
  );
};

export default EnhancedSearchInput;
